import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import useAdmin from '../../hooks/useAdmin';
import axiosInstance from '../../services/axiosInstance';
import Navbar from '../../components/Navbar';

function AdminUsersManagement() {
  const { isAdmin, isLoading } = useAdmin();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all'); // 'all', 'admin', 'user'
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newUser, setNewUser] = useState({
    username: '',
    email: '',
    password: '',
    admin: false
  });
  const [editUser, setEditUser] = useState({
    id: '',
    username: '',
    email: '',
    admin: false
  });
  const [passwordData, setPasswordData] = useState({
    userId: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axiosInstance.get('/admin/partner_users');
        setUsers(response.data.users || []);
        setFilteredUsers(response.data.users || []);
      } catch (error) {
        console.error('Erreur lors du chargement des utilisateurs:', error);
      } finally {
        setLoadingData(false);
      }
    };

    if (!isLoading && isAdmin) {
      fetchUsers();
    }
  }, [isAdmin, isLoading]);

  // Filtrage des utilisateurs
  useEffect(() => {
    let filtered = users;

    // Filtrage par terme de recherche
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrage par rôle
    if (filterRole !== 'all') {
      filtered = filtered.filter(user => {
        if (filterRole === 'admin') return user.admin;
        if (filterRole === 'user') return !user.admin;
        return true;
      });
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, filterRole]);

  const handleCreateUser = async () => {
    try {
      if (!newUser.username || !newUser.email || !newUser.password) {
        alert('Veuillez remplir tous les champs obligatoires');
        return;
      }

      const response = await axiosInstance.post('/admin/partner_users', newUser);
      
      // Rafraîchir la liste
      const usersResponse = await axiosInstance.get('/admin/partner_users');
      setUsers(usersResponse.data.users || []);
      
      // Reset du formulaire
      setNewUser({ username: '', email: '', password: '', admin: false });
      setShowCreateModal(false);
      
      alert('Utilisateur créé avec succès !');
    } catch (error) {
      console.error('Erreur lors de la création:', error);
      alert(error.response?.data?.error || 'Erreur lors de la création de l\'utilisateur');
    }
  };

  const handleEditUser = async () => {
    try {
      if (!editUser.username || !editUser.email) {
        alert('Veuillez remplir tous les champs obligatoires');
        return;
      }

      await axiosInstance.put(`/admin/partner_users/${editUser.id}`, {
        username: editUser.username,
        email: editUser.email,
        admin: editUser.admin
      });
      
      // Rafraîchir la liste
      const usersResponse = await axiosInstance.get('/admin/partner_users');
      setUsers(usersResponse.data.users || []);
      
      setShowEditModal(false);
      setSelectedUser(null);
      
      alert('Utilisateur modifié avec succès !');
    } catch (error) {
      console.error('Erreur lors de la modification:', error);
      alert(error.response?.data?.error || 'Erreur lors de la modification de l\'utilisateur');
    }
  };

  const handleChangePassword = async () => {
    try {
      if (!passwordData.newPassword || !passwordData.confirmPassword) {
        alert('Veuillez remplir tous les champs');
        return;
      }

      if (passwordData.newPassword !== passwordData.confirmPassword) {
        alert('Les mots de passe ne correspondent pas');
        return;
      }

      if (passwordData.newPassword.length < 6) {
        alert('Le mot de passe doit contenir au moins 6 caractères');
        return;
      }

      await axiosInstance.put(`/admin/partner_users/${passwordData.userId}/password`, {
        password: passwordData.newPassword
      });
      
      setPasswordData({ userId: '', newPassword: '', confirmPassword: '' });
      setShowPasswordModal(false);
      setSelectedUser(null);
      
      alert('Mot de passe modifié avec succès !');
    } catch (error) {
      console.error('Erreur lors du changement de mot de passe:', error);
      alert(error.response?.data?.error || 'Erreur lors du changement de mot de passe');
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer l'utilisateur "${username}" ? Cette action est irréversible.`)) {
      return;
    }

    try {
      await axiosInstance.delete(`/admin/partner_users/${userId}`);
      
      // Rafraîchir la liste
      const usersResponse = await axiosInstance.get('/admin/partner_users');
      setUsers(usersResponse.data.users || []);
      
      alert('Utilisateur supprimé avec succès !');
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert(error.response?.data?.error || 'Erreur lors de la suppression de l\'utilisateur');
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditUser({
      id: user.id,
      username: user.username,
      email: user.email,
      admin: user.admin
    });
    setShowEditModal(true);
  };

  const openPasswordModal = (user) => {
    setSelectedUser(user);
    setPasswordData({
      userId: user.id,
      newPassword: '',
      confirmPassword: ''
    });
    setShowPasswordModal(true);
  };

  const styles = {
    container: {
      backgroundColor: '#f8fafc',
      minHeight: '100vh',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    },
    header: {
      backgroundColor: '#ffffff',
      padding: '48px 32px',
      borderBottom: '1px solid #e2e8f0',
      marginBottom: '32px'
    },
    headerContent: {
      maxWidth: '1400px',
      margin: '0 auto',
      textAlign: 'center'
    },
    title: {
      fontSize: '40px',
      fontWeight: '700',
      color: '#1e293b',
      margin: '0 0 16px 0',
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    subtitle: {
      fontSize: '18px',
      color: '#64748b',
      margin: 0
    },
    content: {
      maxWidth: '1400px',
      margin: '0 auto',
      padding: '0 32px'
    },
    controlsBar: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '16px',
      marginBottom: '32px',
      alignItems: 'center'
    },
    searchInput: {
      flex: '1',
      minWidth: '300px',
      padding: '12px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '16px',
      outline: 'none'
    },
    filterSelect: {
      padding: '12px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '16px',
      outline: 'none',
      backgroundColor: '#ffffff'
    },
    createButton: {
      backgroundColor: '#059669',
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 24px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out'
    },
    statsBar: {
      display: 'flex',
      gap: '16px',
      marginBottom: '24px'
    },
    statCard: {
      backgroundColor: '#ffffff',
      padding: '16px 24px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      flex: 1,
      textAlign: 'center'
    },
    statNumber: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '4px'
    },
    statLabel: {
      fontSize: '14px',
      color: '#64748b'
    },
    usersTable: {
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      overflow: 'hidden'
    },
    tableHeader: {
      backgroundColor: '#f8fafc',
      padding: '16px 24px',
      borderBottom: '1px solid #e2e8f0',
      fontWeight: '600',
      color: '#374151',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 120px 100px 180px',
      gap: '16px',
      alignItems: 'center'
    },
    userRow: {
      padding: '16px 24px',
      borderBottom: '1px solid #f1f5f9',
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 120px 100px 180px',
      gap: '16px',
      alignItems: 'center',
      transition: 'background-color 0.2s ease-in-out'
    },
    userName: {
      fontSize: '16px',
      fontWeight: '500',
      color: '#1e293b'
    },
    userEmail: {
      fontSize: '14px',
      color: '#64748b'
    },
    roleBadge: {
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '500'
    },
    adminBadge: {
      backgroundColor: '#fef3c7',
      color: '#d97706'
    },
    userBadge: {
      backgroundColor: '#dbeafe',
      color: '#2563eb'
    },
    establishmentCount: {
      fontSize: '14px',
      color: '#64748b',
      textAlign: 'center'
    },
    actionsContainer: {
      display: 'flex',
      gap: '8px'
    },
    actionButton: {
      padding: '6px 12px',
      border: 'none',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out'
    },
    editButton: {
      backgroundColor: '#f59e0b',
      color: '#ffffff'
    },
    passwordButton: {
      backgroundColor: '#3b82f6',
      color: '#ffffff'
    },
    deleteButton: {
      backgroundColor: '#dc2626',
      color: '#ffffff'
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    },
    modalContent: {
      backgroundColor: '#ffffff',
      borderRadius: '16px',
      padding: '32px',
      width: '100%',
      maxWidth: '500px',
      boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
      maxHeight: '90vh',
      overflowY: 'auto'
    },
    modalTitle: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#1e293b',
      marginBottom: '24px',
      textAlign: 'center'
    },
    inputGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: '500',
      color: '#374151',
      marginBottom: '8px'
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '16px',
      outline: 'none',
      backgroundColor: '#ffffff',
      boxSizing: 'border-box'
    },
    checkbox: {
      width: '16px',
      height: '16px',
      marginRight: '8px'
    },
    checkboxGroup: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '20px'
    },
    buttonGroup: {
      display: 'flex',
      gap: '12px',
      marginTop: '32px'
    },
    button: {
      backgroundColor: '#059669',
      color: '#ffffff',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 24px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      flex: 1
    },
    secondaryButton: {
      backgroundColor: '#f1f5f9',
      color: '#475569',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 24px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      flex: 1
    },
    emptyState: {
      textAlign: 'center',
      padding: '80px 20px',
      color: '#64748b'
    },
    emptyStateIcon: {
      fontSize: '64px',
      marginBottom: '24px'
    },
    emptyStateTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '8px'
    },
    emptyStateText: {
      fontSize: '16px',
      color: '#6b7280'
    },
    backButton: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      backgroundColor: '#f1f5f9',
      color: '#475569',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 20px',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      textDecoration: 'none',
      marginBottom: '24px',
      transition: 'all 0.2s ease-in-out'
    }
  };

  if (isLoading || loadingData) {
    return (
      <div style={styles.container}>
        <Navbar />
        <div style={{ ...styles.content, textAlign: 'center', paddingTop: '100px' }}>
          <div style={{ fontSize: '18px', color: '#64748b' }}>Chargement...</div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={styles.container}>
        <Navbar />
        <div style={{ ...styles.content, textAlign: 'center', paddingTop: '100px' }}>
          <div style={{ fontSize: '18px', color: '#dc2626' }}>Accès non autorisé</div>
        </div>
      </div>
    );
  }

  const adminCount = users.filter(user => user.admin).length;
  const userCount = users.filter(user => !user.admin).length;

  return (
    <div style={styles.container}>
      <Navbar />
      
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <h1 style={styles.title}>Gestion des Utilisateurs Partenaires</h1>
          <p style={styles.subtitle}>
            Gérez les utilisateurs, leurs rôles et leurs accès aux établissements
          </p>
        </div>
      </div>

      <div style={styles.content}>
        <Link
          to="/accueil"
          style={styles.backButton}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#e2e8f0';
            e.target.style.color = '#334155';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#f1f5f9';
            e.target.style.color = '#475569';
          }}
        >
          ← Retour à l'accueil
        </Link>

        {/* Statistiques */}
        <div style={styles.statsBar}>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{users.length}</div>
            <div style={styles.statLabel}>Utilisateurs total</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{adminCount}</div>
            <div style={styles.statLabel}>Administrateurs</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{userCount}</div>
            <div style={styles.statLabel}>Utilisateurs standard</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statNumber}>{filteredUsers.length}</div>
            <div style={styles.statLabel}>Affichés</div>
          </div>
        </div>

        {/* Barre de contrôles */}
        <div style={styles.controlsBar}>
          <input
            type="text"
            placeholder="Rechercher par nom ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
          
          <select
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            style={styles.filterSelect}
          >
            <option value="all">Tous les rôles</option>
            <option value="admin">Administrateurs</option>
            <option value="user">Utilisateurs</option>
          </select>
          
          <button
            style={styles.createButton}
            onClick={() => setShowCreateModal(true)}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#047857'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#059669'}
          >
            + Créer un utilisateur
          </button>
        </div>

        {/* Tableau des utilisateurs */}
        {filteredUsers.length > 0 ? (
          <div style={styles.usersTable}>
            <div style={styles.tableHeader}>
              <div>Nom d'utilisateur</div>
              <div>Email</div>
              <div>Rôle</div>
              <div>Établissements</div>
              <div>Actions</div>
            </div>
            
            {filteredUsers.map((user) => (
              <div
                key={user.id}
                style={styles.userRow}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
              >
                <div>
                  <div style={styles.userName}>{user.username}</div>
                  <div style={{ fontSize: '12px', color: '#9ca3af' }}>ID: {user.id}</div>
                </div>
                
                <div style={styles.userEmail}>{user.email}</div>
                
                <div>
                  <span style={{
                    ...styles.roleBadge,
                    ...(user.admin ? styles.adminBadge : styles.userBadge)
                  }}>
                    {user.admin ? 'Admin' : 'User'}
                  </span>
                </div>
                
                <div style={styles.establishmentCount}>
                  {user.establishments ? user.establishments.length : 0}
                </div>
                
                <div style={styles.actionsContainer}>
                  <button
                    style={{ ...styles.actionButton, ...styles.editButton }}
                    onClick={() => openEditModal(user)}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#d97706'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#f59e0b'}
                  >
                    Modifier
                  </button>
                  
                  <button
                    style={{ ...styles.actionButton, ...styles.passwordButton }}
                    onClick={() => openPasswordModal(user)}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                  >
                    MDP
                  </button>
                  
                  {!user.admin && (
                    <button
                      style={{ ...styles.actionButton, ...styles.deleteButton }}
                      onClick={() => handleDeleteUser(user.id, user.username)}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#b91c1c'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#dc2626'}
                    >
                      Suppr.
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={styles.emptyState}>
            <div style={styles.emptyStateIcon}>👥</div>
            <h3 style={styles.emptyStateTitle}>
              {searchTerm || filterRole !== 'all' ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur'}
            </h3>
            <p style={styles.emptyStateText}>
              {searchTerm || filterRole !== 'all' 
                ? 'Essayez de modifier vos critères de recherche'
                : 'Commencez par créer votre premier utilisateur partenaire'
              }
            </p>
          </div>
        )}
      </div>

      {/* Modal de création d'utilisateur */}
      {showCreateModal && (
        <div style={styles.modal} onClick={() => setShowCreateModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Créer un nouvel utilisateur</h2>
            
            <div style={styles.inputGroup}>
              <label style={styles.label}>Nom d'utilisateur *</label>
              <input
                type="text"
                value={newUser.username}
                onChange={(e) => setNewUser({...newUser, username: e.target.value})}
                style={styles.input}
                placeholder="Nom d'utilisateur"
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Email *</label>
              <input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                style={styles.input}
                placeholder="email@exemple.com"
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Mot de passe *</label>
              <input
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                style={styles.input}
                placeholder="Mot de passe"
              />
            </div>

            <div style={styles.checkboxGroup}>
              <input
                type="checkbox"
                checked={newUser.admin}
                onChange={(e) => setNewUser({...newUser, admin: e.target.checked})}
                style={styles.checkbox}
              />
              <label style={styles.label}>Administrateur</label>
            </div>

            <div style={styles.buttonGroup}>
              <button
                style={styles.secondaryButton}
                onClick={() => {
                  setShowCreateModal(false);
                  setNewUser({ username: '', email: '', password: '', admin: false });
                }}
              >
                Annuler
              </button>
              <button
                style={styles.button}
                onClick={handleCreateUser}
              >
                Créer l'utilisateur
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de modification d'utilisateur */}
      {showEditModal && selectedUser && (
        <div style={styles.modal} onClick={() => setShowEditModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Modifier {selectedUser.username}</h2>
            
            <div style={styles.inputGroup}>
              <label style={styles.label}>Nom d'utilisateur *</label>
              <input
                type="text"
                value={editUser.username}
                onChange={(e) => setEditUser({...editUser, username: e.target.value})}
                style={styles.input}
                placeholder="Nom d'utilisateur"
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Email *</label>
              <input
                type="email"
                value={editUser.email}
                onChange={(e) => setEditUser({...editUser, email: e.target.value})}
                style={styles.input}
                placeholder="email@exemple.com"
              />
            </div>

            <div style={styles.checkboxGroup}>
              <input
                type="checkbox"
                checked={editUser.admin}
                onChange={(e) => setEditUser({...editUser, admin: e.target.checked})}
                style={styles.checkbox}
              />
              <label style={styles.label}>Administrateur</label>
            </div>

            <div style={styles.buttonGroup}>
              <button
                style={styles.secondaryButton}
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedUser(null);
                }}
              >
                Annuler
              </button>
              <button
                style={styles.button}
                onClick={handleEditUser}
              >
                Sauvegarder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de changement de mot de passe */}
      {showPasswordModal && selectedUser && (
        <div style={styles.modal} onClick={() => setShowPasswordModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Changer le mot de passe de {selectedUser.username}</h2>
            
            <div style={styles.inputGroup}>
              <label style={styles.label}>Nouveau mot de passe *</label>
              <input
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                style={styles.input}
                placeholder="Nouveau mot de passe"
              />
            </div>

            <div style={styles.inputGroup}>
              <label style={styles.label}>Confirmer le mot de passe *</label>
              <input
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                style={styles.input}
                placeholder="Confirmer le mot de passe"
              />
            </div>

            <div style={styles.buttonGroup}>
              <button
                style={styles.secondaryButton}
                onClick={() => {
                  setShowPasswordModal(false);
                  setSelectedUser(null);
                  setPasswordData({ userId: '', newPassword: '', confirmPassword: '' });
                }}
              >
                Annuler
              </button>
              <button
                style={styles.button}
                onClick={handleChangePassword}
              >
                Changer le mot de passe
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminUsersManagement;